/**
 * Reads for the seller-parity admin endpoints (plan Phase 2/3). Keys follow
 * `[admin, stores, <resource>, storeid, params]` so `isStoreScopedKey` can
 * refresh every view of a store after a write.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import useGetQueryFn from '../use-get-query-fn';
import { adminStoreKeys, unwrap } from './stores.query';
import type {
  StoreAnalyticsSeries,
  StoreAttributeSpec,
  StoreDeliveryCoverage,
  StoreEngagement,
  StoreMember,
  StoreMemberPermissions,
  StoreInventoryResponse,
  StoreVariantSuggestionsResponse,
  StoreCatalogListing,
  StoreInvoice,
  StoreInvoicesResponse,
  StoreOrderDetail,
  StoreOrdersResponse,
  StorePayoutBalance,
  StorePriceHistoryResponse,
  StoreProductsResponse,
  StoreRefundRequestsResponse,
  StoreRenewal,
  StoreReturnRequestsResponse,
  StoreThreadResponse,
  StoreThreadsResponse,
  StoreTransactionSummary,
  StoreTransactionsResponse,
  StoreVouchersResponse,
  StoreWalletResponse,
} from '@/client/types/stores/parity';

const BASE = '/api/admin/stores';

type Params = Record<string, string | number | boolean | undefined | null>;

const key = (resource: string, storeid: number | null, params: Params = {}) =>
  [...adminStoreKeys.all, resource, storeid, params] as const;

const clean = (params: Params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

/** One GET under `/api/admin/stores/<id>/<path>`, unwrapped from the proxy envelope. */
function useStoreGet<T>(
  resource: string,
  storeid: number | null,
  path: string,
  fallback: T,
  params: Params = {},
  opts: { enabled?: boolean; keepPrevious?: boolean; errorMessage?: string } = {},
) {
  const queryFn = useGetQueryFn({
    endpoint: `${BASE}/${storeid}${path}`,
    defaultErrorMessage: opts.errorMessage ?? 'Failed to load store data',
  });
  return useQuery({
    queryKey: key(resource, storeid, params),
    queryFn: async (): Promise<T> => unwrap<T>(await queryFn(clean(params)), fallback),
    enabled: storeid !== null && (opts.enabled ?? true),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: opts.keepPrevious ? keepPreviousData : undefined,
  });
}

const EMPTY_PAGE = { total_count: 0, limit: 0, offset: 0 };

// ── Orders ──────────────────────────────────────────────────────────────────

export interface StoreOrderListParams extends Params {
  status?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort?: 'date_added_desc' | 'date_added_asc' | 'amount_total_desc';
  limit?: number;
  offset?: number;
}

export const useStoreOrderListQuery = (storeid: number | null, params: StoreOrderListParams = {}) =>
  useStoreGet<StoreOrdersResponse>('orders', storeid, '/orders', { orders: [], ...EMPTY_PAGE }, params, {
    keepPrevious: true, errorMessage: 'Failed to load orders',
  });

export const useStoreOrderDetailQuery = (storeid: number | null, orderid: number | null) =>
  useStoreGet<StoreOrderDetail | null>('order', storeid, `/orders/${orderid}`, null, {}, {
    enabled: orderid !== null, errorMessage: 'Failed to load order',
  });

export const useStoreRefundRequestsQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreRefundRequestsResponse>('refund-requests', storeid, '/refund-requests',
    { refund_requests: [], ...EMPTY_PAGE }, params, { keepPrevious: true });

export const useStoreReturnRequestsQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreReturnRequestsResponse>('return-requests', storeid, '/return-requests',
    { return_requests: [], ...EMPTY_PAGE }, params, { keepPrevious: true });

// ── Inventory & pricing ─────────────────────────────────────────────────────

export const useStoreInventoryQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreInventoryResponse>('inventory', storeid, '/inventory', { inventory: [], ...EMPTY_PAGE }, params, {
    keepPrevious: true, errorMessage: 'Failed to load inventory',
  });

export const useStoreVariantSuggestionsQuery = (storeid: number | null) =>
  useStoreGet<StoreVariantSuggestionsResponse>('variant-suggestions', storeid, '/variants/suggestions', { suggestions: [] });

export const useStoreCatalogListingQuery = (storeid: number | null, eid: number | null) =>
  useStoreGet<StoreCatalogListing | null>('catalog-listing', storeid, `/listing/${eid}/siblings`, null, { eid }, {
    enabled: eid !== null,
  });

export const useStoreLowStockQuery = (storeid: number | null) =>
  useStoreGet<StoreInventoryResponse>('low-stock', storeid, '/inventory/low-stock', { inventory: [], ...EMPTY_PAGE },
    { limit: 5 });

export const useStoreProductsQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreProductsResponse>('products', storeid, '/products',
    { products: [], available_taxes: [], standard_taxids: [], ...EMPTY_PAGE }, params, {
      keepPrevious: true, errorMessage: 'Failed to load products',
    });

export const useStorePriceHistoryQuery = (storeid: number | null, eid: number | null) =>
  useStoreGet<StorePriceHistoryResponse | null>('price-history', storeid, `/products/${eid}/price-history`, null, {}, {
    enabled: eid !== null,
  });

// ── Money & performance ─────────────────────────────────────────────────────

export interface StoreRangeParams extends Params {
  date_from?: string;
  date_to?: string;
}

export const useStoreTransactionListQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreTransactionsResponse>('transactions', storeid, '/transactions',
    { transactions: [], ...EMPTY_PAGE }, params, { keepPrevious: true, errorMessage: 'Failed to load transactions' });

export const useStoreTransactionSummaryQuery = (storeid: number | null, params: StoreRangeParams = {}) =>
  useStoreGet<StoreTransactionSummary | null>('transactions-summary', storeid, '/transactions/summary', null, params, {
    keepPrevious: true,
  });

export const useStoreAnalyticsSeriesQuery = (
  storeid: number | null,
  params: StoreRangeParams & { bucket?: 'day' | 'week' | 'month' } = {},
) =>
  useStoreGet<StoreAnalyticsSeries | null>('analytics-series', storeid, '/analytics/series', null, params, {
    keepPrevious: true,
  });

export const useStoreEngagementQuery = (storeid: number | null, params: StoreRangeParams & { top?: number } = {}) =>
  useStoreGet<StoreEngagement | null>('analytics-engagement', storeid, '/analytics/engagement', null, params, {
    keepPrevious: true,
  });

export const useStorePayoutBalanceQuery = (storeid: number | null) =>
  useStoreGet<StorePayoutBalance | null>('payout-balance', storeid, '/payout/balance', null);

export const useStoreWalletQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreWalletResponse>('wallet', storeid, '/wallet/transactions', { transactions: [], ...EMPTY_PAGE },
    params, { keepPrevious: true });

export const useStoreRenewalQuery = (storeid: number | null) =>
  useStoreGet<{ renewal: StoreRenewal | null }>('renewal', storeid, '/plan/renewal', { renewal: null });

export const useStoreInvoicesQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreInvoicesResponse>('invoices', storeid, '/invoices', { invoices: [], total_count: 0 }, params, {
    keepPrevious: true,
  });

export const useStoreInvoiceQuery = (storeid: number | null, invid: number | null) =>
  useStoreGet<StoreInvoice | null>('invoice', storeid, `/invoices/${invid}`, null, {}, { enabled: invid !== null });

// ── Vouchers & messages ─────────────────────────────────────────────────────

export const useStoreVouchersQuery = (storeid: number | null, params: Params = {}) =>
  useStoreGet<StoreVouchersResponse>('vouchers', storeid, '/vouchers',
    { vouchers: [], pagination: { total: 0, limit: 0, offset: 0, has_more: false } }, params, { keepPrevious: true });

export const useStoreThreadsQuery = (storeid: number | null, params: Params = {}, enabled = true) =>
  useStoreGet<StoreThreadsResponse>('threads', storeid, '/messages',
    { conversations: [], pagination: { total: 0, limit: 0, offset: 0 } }, params, { keepPrevious: true, enabled });

export const useStoreThreadQuery = (storeid: number | null, conversationKey: string | null) =>
  useStoreGet<StoreThreadResponse | null>('thread', storeid,
    `/messages/${encodeURIComponent(conversationKey ?? '')}`, null, {}, { enabled: conversationKey !== null });

// ── Staff, attributes, delivery ─────────────────────────────────────────────

export const useStoreMembersQuery = (storeid: number | null) =>
  useStoreGet<{ users: StoreMember[] }>('members', storeid, '/members', { users: [] });

export const useStoreMemberPermissionsQuery = (storeid: number | null, uid: number | null) =>
  useStoreGet<StoreMemberPermissions | null>('member-permissions', storeid, `/permissions/users/${uid}`, null, {}, {
    enabled: uid !== null,
  });

/** Store attribute catalogue — the same form ff-ui's Store Manager renders. */
export const useStoreAttributeSpecQuery = () => {
  const queryFn = useGetQueryFn({ endpoint: `${BASE}/attributes/spec`, defaultErrorMessage: 'Failed to load the attribute spec' });
  return useQuery({
    queryKey: [...adminStoreKeys.all, 'attribute-spec'] as const,
    queryFn: async (): Promise<StoreAttributeSpec | null> => unwrap(await queryFn({}), null),
    staleTime: 10 * 60 * 1000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useStoreAttributesQuery = (storeid: number | null) =>
  useStoreGet<{ attributes: Record<string, unknown> }>('attributes', storeid, '/attributes', { attributes: {} });

export const useStoreDeliveryCoverageQuery = (storeid: number | null) =>
  useStoreGet<StoreDeliveryCoverage | null>('delivery', storeid, '/delivery-locations', null);

// ── Branches (ff-api business rev 0510) ─────────────────────────────────────

export interface StoreBranch {
  branch_id: number;
  storeid: number;
  name: string | null;
  is_primary: boolean;
  active: boolean;
  pickup_enabled: boolean;
  eid: number | null;
  venue: { eid: number; name: string | null; slug: string | null; lid: number | null } | null;
  lid: number | null;
  pickup_address: Record<string, unknown> | null;
  latitude: number | null;
  longitude: number | null;
  delivery_locations: number[];
}

export interface StoreBranchStock {
  branch_id: number;
  name: string;
  is_primary: boolean;
  active: boolean;
  quantity: number;
  reserved: number;
  available: number;
}

export const useStoreBranchesQuery = (storeid: number | null) =>
  useStoreGet<{ branches: StoreBranch[] }>('branches', storeid, '/branches', { branches: [] });

/** Where one listing's units are, per branch. */
export const useStoreBranchStockQuery = (storeid: number | null, eid: number | null) =>
  useStoreGet<{ stock: Record<string, StoreBranchStock[]> }>(
    'branch-stock', storeid, '/inventory/branches', { stock: {} }, { eids: eid ?? undefined },
    { enabled: eid !== null });
